import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { Button } from 'components/common';
import UnderlineInput from '../common/UnderlineInput';
import SelectDropdown from '../common/SelectDropdown';
import { getHsbcSettings, saveHsbcSettings } from '../common/settingsApi';
import { SettingsCard } from './settingsKit';

const sections = [
  { key: 'connection', title: 'Connection',
    note: 'Portal → HSBC gateway (gpg-encrypt) → HSBC. Three values from the gateway .env: its URL, GPG_PAYMENT_API_KEY (payments) and GPG_ADMIN_API_KEY (the PGP keys below). The HSBC API credential (HSBC_PROFILE_ID / HSBC_CLIENT_SECRET) is set only in the gateway .env.' },
  { key: 'debtor', title: 'Pebsteel — the payer',
    note: 'Set once, used for every payment (InitgPty, Dbtr, DbtrAcct in the XMLv3 file). Saving the HSBC customer ID switches the XMLv3 format on.' },
  { key: 'defaults', title: 'Payment defaults',
    note: 'Accounting can change them for each payment on the Payment journal - APP tab (transfer type, charges, recipient, bank, bank code, address).' },
];
// Options come as values ("mock") or { value, label } from the server.
const optionOf = (option) => (typeof option === 'object' ? option
  : { value: option, label: option === 'mock' ? 'Mock · testing' : option === 'hsbc_api' ? 'HSBC API' : option });
const sourceLabel = { stored: 'Saved in the Portal', env: 'Server configuration', default: 'Default value', none: 'Not configured' };

const HsbcConfigurationSection = ({ onSaved }) => {
  const [fields, setFields] = useState(null);
  const [values, setValues] = useState({});
  const [pending, setPending] = useState({});
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const apply = ({ data }) => {
    setFields(data);
    setValues(Object.fromEntries(data.map((field) => [field.key, field.value || ''])));
    setPending({});
  };
  const load = useCallback(async () => {
    setError('');
    try { apply(await getHsbcSettings()); }
    catch (e) { setError(e?.response?.data?.error || 'Could not load the HSBC settings.'); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const change = (key, value) => {
    setError('');
    setValues((old) => ({ ...old, [key]: value }));
    setPending((old) => {
      const next = { ...old };
      const field = fields.find((item) => item.key === key);
      if (value === (field.value || '') || (field.type === 'password' && !value.trim())) delete next[key];
      else next[key] = value;
      return next;
    });
  };
  const save = async () => {
    setBusy(true); setError('');
    try {
      const res = await saveHsbcSettings(pending);
      apply(res);
      toast.success(res.message || 'HSBC settings saved.');
      onSaved?.();
    } catch (e) { setError(e?.response?.data?.error || 'Could not save the HSBC settings.'); }
    finally { setBusy(false); }
  };
  return <SettingsCard title="HSBC settings" subtitle="Connection and company details used to create payments.">
    <div className="tw-p-5 tw-space-y-5">
      {error && <div role="alert" className="tw-text-geist-error tw-text-sm">{error}{!fields && <Button onClick={load} variant="default" size="sm">Retry</Button>}</div>}
      {!fields && !error && <p>Loading HSBC settings…</p>}
      {fields && sections.map((section) => <section key={section.key} aria-label={section.title}>
        <h3 className="tw-text-sm tw-font-semibold tw-mb-1">{section.title}</h3>
        {section.note ? <p className="tw-m-0 tw-mb-3 tw-text-xs tw-text-geist-fg-3">{section.note}</p> : <div className="tw-mb-2" />}
        <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
          {fields.filter((field) => field.section === section.key).map((field) => <label key={field.key} className={`tw-block tw-min-w-0 ${field.type === 'textarea' ? 'md:tw-col-span-2' : ''}`}>
            <span className="tw-block tw-text-sm tw-font-medium tw-text-geist-fg-2 tw-mb-1">{field.label}</span>
            {field.type === 'select' ? <SelectDropdown aria-label={field.label} value={values[field.key] || field.default || ''} disabled={busy}
              options={field.options.map(optionOf)}
              onChange={(value) => change(field.key, value)} />
              : field.type === 'textarea' ? <textarea aria-label={field.label} value={values[field.key]} disabled={busy} rows={4}
                className="tw-w-full tw-rounded-geist-sm tw-border tw-border-solid tw-border-geist-border tw-p-3 tw-text-sm"
                placeholder={'BIDV = BIDVVNVX\nor JSON: {"BIDV":"BIDVVNVX"}'} onChange={(event) => change(field.key, event.target.value)} />
                : <UnderlineInput aria-label={field.label} type={field.type} value={values[field.key]} disabled={busy}
                  autoComplete={field.type === 'password' ? 'new-password' : 'off'}
                  placeholder={field.type === 'password' ? (field.configured ? '•••••• (saved) — type to replace' : 'Enter a value') : ''}
                  onChange={(event) => change(field.key, event.target.value)} />}
            <span className="tw-block tw-text-xs tw-text-geist-fg-3 tw-mt-1">
              {field.type === 'password'
                ? <span className={field.configured ? 'tw-text-[#0a7d33]' : 'tw-text-[#a05e03] tw-font-semibold'}>
                    {field.configured ? 'Configured · leave blank to keep it' : 'Not configured'}</span>
                : sourceLabel[field.source]}
              {field.hint ? ` · ${field.hint}` : ''}
            </span>
          </label>)}
        </div>
      </section>)}
      {/* One save for the whole form, kept in view while scrolling. */}
      <div className="tw-sticky tw-bottom-0 tw-flex tw-items-center tw-justify-end tw-gap-3 tw-py-3 tw-bg-geist-bg tw-border-0 tw-border-t tw-border-solid tw-border-geist-border">
        {Object.keys(pending).length > 0 && <span className="tw-text-xs tw-text-geist-fg-3">{Object.keys(pending).length} unsaved change{Object.keys(pending).length > 1 ? 's' : ''}</span>}
        <Button variant="primary" size="md" disabled={busy || !Object.keys(pending).length} onClick={save}>
        {busy ? 'Saving…' : 'Save HSBC settings'}
      </Button></div>
    </div>
  </SettingsCard>;
};
HsbcConfigurationSection.propTypes = { onSaved: PropTypes.func };
export default HsbcConfigurationSection;
